import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';

import type { ForYouConfig } from '../../../common/forYouTypes';
import type { NavItemConfig, ObjectListFlyoutItemConfig } from '../../../common/types';
import { omitSafely } from '../../../common/utils/omit-safely';
import type { Action } from '../../../ui/object-list-flyout/types';

import { STARRED_TEST_ID } from './constants';
import { messages } from './messages';

const elemBefore = <StarUnstarredIcon label="" />;

export function useStarredNavItem(forYou: ForYouConfig): [string, NavItemConfig][] {
	const { formatMessage } = useIntl();
	const starredConfig = forYou?.starred;

	return useMemo(() => {
		if (!starredConfig) {
			return [];
		} else {
			const actions: Action[] | undefined = starredConfig.viewAllUrl
				? [
						{
							elemBefore: <AlignTextLeftIcon label={''} />,
							label: formatMessage(messages.viewAllStarredItemsLabel),
							href: starredConfig.viewAllUrl,
							onClick: starredConfig.viewAllOnClick,
						},
					]
				: starredConfig.viewAllOnClick && [
						{
							elemBefore: <AlignTextLeftIcon label={''} />,
							label: formatMessage(messages.viewAllStarredItemsLabel),
							onClick: starredConfig.viewAllOnClick,
						},
					];

			// Omit the type-props from FlyoutProps that are not used by ObjectListFlyout
			const safeConfig = omitSafely(starredConfig, 'menuItemId', 'viewAllOnClick', 'viewAllUrl');

			const value: ObjectListFlyoutItemConfig = {
				...safeConfig,
				actions,
				elemBefore,
				label: formatMessage(messages.label),
				searchPlaceholder: formatMessage(messages.searchPlaceholder),
				testId: STARRED_TEST_ID,
				type: 'object-list-flyout',
			};

			return [[starredConfig.menuItemId ?? 'global.sidebar.starred', value]];
		}
	}, [formatMessage, starredConfig]);
}
